import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { KycCaseResponse, KycDecisionInput, RecordKycScreenings } from "@dallali/shared";
import { usePublicFeatureFlag } from "@/hooks/useFeatureFlags";
import { usePortalBase } from "@/hooks/usePortalBase";
import { kycComplianceRepository } from "@/repositories/kycCompliance";

export const KYC_COMPLIANCE_KEYS = {
  access: ["kyc-compliance", "access"] as const,
  queue: ["kyc-compliance", "queue"] as const,
  case: (profileId: string) => ["kyc-compliance", "case", profileId] as const,
};

/**
 * The caller's EXPLICIT compliance grants in the MAIN company (the API's
 * ComplianceGrantGuard rule — no admin fast-path, unlike usePermissions).
 * Fail-closed: false while the flag or the answer is loading, on an error, and
 * whenever `kyc_onboarding` is off. The dashboard is signed-in only, so the
 * flag alone gates the request.
 */
export function useKycComplianceAccess() {
  const { data: flagOn, isLoading: flagLoading } = usePublicFeatureFlag("kyc_onboarding");
  const query = useQuery({
    queryKey: KYC_COMPLIANCE_KEYS.access,
    queryFn: () => kycComplianceRepository.access(),
    enabled: flagOn === true,
    staleTime: 5 * 60_000,
    retry: false,
  });
  return {
    review: flagOn === true && query.data?.review === true,
    approveHigh: flagOn === true && query.data?.approveHigh === true,
    isLoading: flagLoading || (flagOn === true && query.isLoading),
  };
}

/** The queue's path in the portal the reader is in (dashboard or employee portal). */
export function useKycComplianceBase(): string {
  return `${usePortalBase()}/compliance/kyc`;
}

export function useKycQueue(enabled: boolean) {
  return useQuery({
    queryKey: KYC_COMPLIANCE_KEYS.queue,
    queryFn: () => kycComplianceRepository.queue(),
    enabled,
    staleTime: 30_000,
  });
}

/** One case. Never cached for long: every read is an audited view of a person's file. */
export function useKycCase(profileId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: KYC_COMPLIANCE_KEYS.case(profileId ?? ""),
    queryFn: () => kycComplianceRepository.getCase(profileId!),
    enabled: enabled && !!profileId,
    staleTime: 0,
    gcTime: 0,
  });
}

/** After a write the API returns the updated case; the queue moves on its own. */
function useCaseWrite<TBody>(profileId: string, write: (id: string, body: TBody) => Promise<KycCaseResponse>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) => write(profileId, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(KYC_COMPLIANCE_KEYS.case(profileId), updated);
      void queryClient.invalidateQueries({ queryKey: KYC_COMPLIANCE_KEYS.queue });
    },
  });
}

export const useRecordKycScreenings = (profileId: string) =>
  useCaseWrite<RecordKycScreenings>(profileId, kycComplianceRepository.recordScreenings);

export const useKycDecision = (profileId: string) => useCaseWrite<KycDecisionInput>(profileId, kycComplianceRepository.decide);
